import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, Spin, Table, Tag } from "antd";
import { useState } from "react";
import { getPayouts, getPayoutsHistory } from "../actions/acc_actions";
import AddPayoutModal from "../components/AddPayoutModal";
import ContentLayout from "../components/layout/ContentLayout";

export default function Payouts() {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const { isLoading, data, isError, refetch } = useQuery({
    queryKey: ["walletAccounts", page, limit],
    queryFn: () => getPayouts({ page, limit }),
    keepPreviousData: true,
  });

  if (isLoading) {
    return (
      <div className="wallet-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert
        type="error"
        message="Failed to load wallet data"
        description="Please try again later."
      />
    );
  }

  const accounts = data?.body?.docs || [];
  const pagination = data?.body?.pagination || {};

  const tableData = accounts.map((item) => ({
    key: item.accountUrn,
    accountUrn: item.accountUrn,
    accountName: item.account?.name || "-",
    clearedBalance: Number(item.clearedBalance),
    pendingBalance: Number(item.pendingBalance),
  }));

  const columns = [
    {
      title: "Account",
      dataIndex: "accountName",
    },
    {
      title: "Cleared Balance",
      dataIndex: "clearedBalance",
      render: (v) => <Tag color="green">${v.toFixed(2)}</Tag>,
    },
    {
      title: "Pending Balance",
      dataIndex: "pendingBalance",
      render: (v) => <Tag color="orange">${v.toFixed(2)}</Tag>,
    },
    {
      title: "Total Balance",
      render: (_, record) => {
        const total = record.clearedBalance + record.pendingBalance;
        return <strong>${total.toFixed(2)}</strong>;
      },
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setSelectedAccount(record.accountUrn)}
        >
          Add Payout
        </Button>
      ),
    },
  ];

  // 🔥 Nested table renderer
  const ExpandedRow = ({ accountUrn }) => {
    const { data, isLoading } = useQuery({
      queryKey: ["payoutHistory", accountUrn],
      queryFn: () => getPayoutsHistory(accountUrn),
      enabled: !!accountUrn,
    });

    console.log(data);

    const historyColumns = [
      {
        title: "Product",
        render: (_, record) => record?.order_item?.product?.name_en || "-",
      },
      {
        title: "Type",
        dataIndex: "transaction_type",
        render: (type) => (
          <Tag color={type === "sale" ? "blue" : "purple"}>
            {type?.toUpperCase()}
          </Tag>
        ),
      },
      {
        title: "Gross",
        dataIndex: "amount",
        render: (v) => <Tag color="geekblue">${Number(v).toFixed(2)}</Tag>,
      },
      {
        title: "Transaction Fee",
        dataIndex: "commission_amount",
        render: (v) => <Tag color="red">-${Number(v).toFixed(2)}</Tag>,
      },
      {
        title: "Net",
        render: (_, record) => {
          const net = Number(record.amount) - Number(record.commission_amount);
          return (
            <strong style={{ color: "#16a34a" }}>${net.toFixed(2)}</strong>
          );
        },
      },
      {
        title: "Status",
        dataIndex: "status",
        render: (status) => {
          const color =
            status === "cleared"
              ? "green"
              : status === "pending"
                ? "orange"
                : "default";

          return <Tag color={color}>{status?.toUpperCase()}</Tag>;
        },
      },
      {
        title: "Date",
        dataIndex: "created_at",
        render: (date) => new Date(date).toLocaleString(),
      },
    ];

    if (isLoading) return <Spin />;

    return (
      <Table
        columns={historyColumns}
        dataSource={data}
        rowKey="id"
        pagination={false}
        size="small"
        bordered
      />
    );
  };

  return (
    <ContentLayout
      header={
        <>
          <h1>
            Wallet <br /> <span>Your Financial Overview</span>
          </h1>
        </>
      }
    >
      <Card className="wallet-table-card">
        <h3>Accounts</h3>

        <Table
          columns={columns}
          dataSource={tableData}
          expandable={{
            expandedRowRender: (record) => (
              <ExpandedRow accountUrn={record.accountUrn} />
            ),
            expandedRowKeys,
            onExpandedRowsChange: setExpandedRowKeys,
          }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            onChange: (newPage) => setPage(newPage),
          }}
          scroll={{ x: 600 }}
        />
      </Card>

      <AddPayoutModal
        open={!!selectedAccount}
        accountUrn={selectedAccount}
        onClose={() => setSelectedAccount(null)}
        onSuccess={() => {
          refetch();
        }}
      />
    </ContentLayout>
  );
}
